<!--
  Copyright (c) 2025 NoorWays. All rights reserved.
  Licensed under the terms specified in the LICENSE.txt file.
-->

<template>
  <div class="plan-selector">
    <div class="plans-grid">
      <div
        v-for="plan in plans"
        :key="plan.id"
        class="plan-card"
        :class="{
          recommended: plan.id === recommendedPlan,
          current: plan.id === currentPlan,
          disabled: disabled,
        }"
      >
        <!-- Recommended Badge -->
        <div v-if="plan.id === recommendedPlan" class="recommended-badge">
          {{ $t('subscription.recommended') }}
        </div>

        <!-- Current Plan Badge -->
        <div v-if="plan.id === currentPlan" class="current-badge">
          {{ $t('subscription.current') }}
        </div>

        <!-- Plan Header -->
        <div class="plan-header">
          <h3>{{ plan.name }}</h3>
          <div class="plan-price">
            <span class="amount">{{
              $n(plan.price, { style: 'currency', currency: plan.currency })
            }}</span>
            <span class="interval">/ {{ getIntervalText(plan.interval) }}</span>
          </div>
          <div v-if="plan.savings" class="savings">
            {{ $t('subscription.savePrefix') }} {{ plan.savings }}
          </div>
        </div>

        <!-- Plan Features -->
        <div class="plan-features">
          <!-- Use standardized premium benefits for premium plans -->
          <PremiumBenefits
            v-if="plan.id !== 'free'"
            :show-title="false"
            class="premium-benefits-display"
          />

          <!-- Use traditional feature list for free plan -->
          <ul v-else class="features-list">
            <li v-for="feature in plan.features" :key="feature">
              <span class="feature-icon">✓</span>
              {{ feature }}
            </li>
          </ul>
        </div>

        <!-- Plan Action -->
        <div class="plan-action">
          <button
            v-if="plan.id !== currentPlan"
            @click="selectPlan(plan.id)"
            class="btn btn-primary btn-full-width"
            :disabled="disabled || isLoading"
          >
            <span v-if="isLoading">{{ $t('common.loading') }}</span>
            <span v-else-if="plan.id === 'free'">{{ $t('subscription.selectFree') }}</span>
            <span v-else>{{ $t('subscription.selectPlan') }}</span>
          </button>
          <div v-else class="current-plan-indicator">
            <span class="current-icon">✓</span>
            {{ $t('subscription.currentPlan') }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import type { SubscriptionPlanDefinition, SubscriptionPlanType } from '@/types/subscription'
import PremiumBenefits from './PremiumBenefits.vue'

interface Props {
  plans: SubscriptionPlanDefinition[]
  currentPlan?: SubscriptionPlanType | string
  recommendedPlan?: string
  disabled?: boolean
  isLoading?: boolean
}

interface Emits {
  'select-plan': [planId: string]
}

const props = withDefaults(defineProps<Props>(), {
  currentPlan: 'free',
  recommendedPlan: 'premium-yearly',
  disabled: false,
  isLoading: false,
})

const emit = defineEmits<Emits>()
const { t } = useI18n()

/**
 * Get localized interval text
 */
function getIntervalText(interval: 'month' | 'year'): string {
  return interval === 'month' ? t('subscription.month') : t('subscription.year')
}

/**
 * Handle plan selection
 */
function selectPlan(planId: string): void {
  if (!props.disabled && !props.isLoading) {
    emit('select-plan', planId)
  }
}
</script>

<style lang="scss" scoped>
.plan-selector {
  width: 100%;
}

.plans-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  max-width: 1000px;
  margin: 0 auto;
}

.plan-card {
  background: var(--nw-bg-primary);
  border: 1px solid var(--nw-border-light);
  border-radius: 1.5rem;
  padding: 2.5rem 2rem;
  position: relative;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  flex-direction: column;
  height: 100%;
  backdrop-filter: blur(10px);
}

.plan-card:hover:not(.disabled) {
  transform: translateY(-8px);
  box-shadow:
    0 20px 40px rgba(0, 0, 0, 0.1),
    0 0 0 1px var(--nw-primary-20);
}

.plan-card.recommended {
  border: 2px solid var(--nw-primary);
  background: linear-gradient(
    135deg,
    rgba(30, 64, 175, 0.05) 0%,
    rgba(30, 64, 175, 0.02) 100%
  );
  box-shadow:
    0 10px 40px var(--nw-primary-20),
    0 0 60px rgba(30, 64, 175, 0.1);
  transform: scale(1.02);

  &:hover:not(.disabled) {
    transform: scale(1.02) translateY(-8px);
    box-shadow:
      0 25px 50px var(--nw-primary-20),
      0 0 80px rgba(30, 64, 175, 0.15);
  }
}

.plan-card.current {
  border-color: var(--nw-success);
  background: linear-gradient(
    135deg,
    rgba(34, 197, 94, 0.05) 0%,
    rgba(34, 197, 94, 0.02) 100%
  );
}

.plan-card.disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.recommended-badge {
  position: absolute;
  top: -14px;
  left: 50%;
  transform: translateX(-50%);
  background: linear-gradient(135deg, var(--nw-primary) 0%, var(--nw-primary-dark) 100%);
  color: var(--nw-white);
  padding: 0.5rem 1.5rem;
  border-radius: 2rem;
  font-size: 0.8rem;
  font-weight: 700;
  white-space: nowrap;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  box-shadow: 0 4px 15px var(--nw-primary-40);
}

.current-badge {
  position: absolute;
  top: -14px;
  left: 50%;
  transform: translateX(-50%);
  background: linear-gradient(135deg, var(--nw-success) 0%, #16a34a 100%);
  color: var(--nw-white);
  padding: 0.5rem 1.5rem;
  border-radius: 2rem;
  font-size: 0.8rem;
  font-weight: 700;
  white-space: nowrap;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.plan-header {
  text-align: center;
  margin-bottom: 2rem;
}

.plan-header h3 {
  font-size: 1.5rem;
  font-weight: 800;
  color: var(--nw-text-primary);
  margin-bottom: 1rem;
}

.plan-price {
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: 0.25rem;
  margin-bottom: 0.5rem;
}

.currency {
  font-size: 1.25rem;
  color: var(--nw-text-secondary);
  font-weight: 600;
}

.amount {
  font-size: 3rem;
  font-weight: 800;
  color: var(--nw-text-primary);
  line-height: 1;
  background: linear-gradient(135deg, var(--nw-text-primary) 0%, var(--nw-primary) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.plan-card.recommended .amount {
  background: linear-gradient(135deg, var(--nw-primary) 0%, var(--nw-primary-dark) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.interval {
  font-size: 1rem;
  color: var(--nw-text-secondary);
  font-weight: 500;
}

.savings {
  background: linear-gradient(135deg, rgba(251, 191, 36, 0.15) 0%, rgba(245, 158, 11, 0.1) 100%);
  color: #b45309;
  padding: 0.375rem 1rem;
  border-radius: var(--nw-radius-xl);
  font-size: 0.8rem;
  font-weight: 700;
  display: inline-block;
  border: 1px solid rgba(245, 158, 11, 0.3);
}

.plan-features {
  margin: 0 0 2rem 0;
  flex-grow: 1;
}

.features-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.features-list li {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 0.75rem 0;
  color: var(--nw-text-secondary);
  line-height: 1.5;
}

.premium-benefits-display {
  padding: 0.5rem 0;
}

.feature-icon {
  color: var(--nw-success);
  font-weight: bold;
  flex-shrink: 0;
  margin-top: 0.125rem;
  width: 20px;
  height: 20px;
  background: rgba(34, 197, 94, 0.1);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
}

.plan-action {
  margin-top: auto;
}

.btn {
  padding: 1rem 2rem;
  border: none;
  border-radius: 1rem;
  font-weight: 700;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  text-decoration: none;
  display: inline-block;
  text-align: center;
}

.btn-primary {
  background: linear-gradient(135deg, var(--nw-primary) 0%, var(--nw-primary-dark) 100%);
  color: var(--nw-white);
  box-shadow: 0 4px 15px var(--nw-primary-40);
}

.btn-primary:hover:not(:disabled) {
  transform: translateY(-3px);
  box-shadow: 0 8px 25px var(--nw-primary-40);
}

.btn-primary:active:not(:disabled) {
  transform: translateY(-1px);
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

.btn-full-width {
  width: 100%;
}

.current-plan-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 1rem 2rem;
  background: linear-gradient(135deg, var(--nw-success) 0%, #16a34a 100%);
  color: var(--nw-white);
  border-radius: 1rem;
  font-weight: 700;
}

.current-icon {
  font-size: 1.25rem;
}

@media (max-width: 768px) {
  .plans-grid {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }

  .plan-card {
    padding: 2rem 1.5rem;
  }

  .plan-card.recommended {
    transform: none;

    &:hover:not(.disabled) {
      transform: translateY(-8px);
    }
  }

  .amount {
    font-size: 2.5rem;
  }
}
</style>
